import * as React from 'react'
import { connect } from 'react-redux'
import { strings } from '../../lib/localization'
import throttle from 'lodash/throttle'
import { EmailTranscript } from './emailtranscript'
import { withOutsideClick } from '../withoutsideclick'
import {
  sendMessage,
  sendTyping,
  sendTypingStopped,
  uploadAndSendFile,
  updateMessageListHeight
} from '../../actions/messages'
import { showConsent } from '../../actions/consent'
import { addPendingMessage } from '../../actions/pendingmessages'
import { saveEditorContent } from '../../actions/activeconversation'
import { shouldShowConsentSelector } from '../../selectors/consent'
import { RootState } from '../../types/rootstate'
import { isMobileSelector } from '../../selectors/widget'
import { LoadableEmojiPicker } from './emoji-picker-loader'

import '../../sass/online/footer.scss'

const EmailTranscriptPopover = withOutsideClick(EmailTranscript)

interface StateProps {
  isActive: boolean
  isClosedByMe: boolean
  agentName: string
  shouldShowConsent: boolean
  content: string
  isMobile: boolean
}

interface DispatchProps {
  sendMessage: (message: string) => void
  sendTyping: (message: string) => void
  sendTypingStopped: () => void
  uploadAndSendFile: (file: File) => void
  updateMessageListHeight: (newHeight: number) => void
  showConsent: () => void
  addPendingMessage: (message: string) => void
  saveEditorContent: (content: string) => void
}

interface OwnProps {
  afterSendMessage?: () => void
  onChange?: () => void
  onAddFile?: () => void
}

type FooterProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState): StateProps {
  return {
    isActive: state.activeConversation.isActive,
    isClosedByMe: state.activeConversation.isClosedByMe,
    agentName: state.activeConversation.agentName,
    shouldShowConsent: shouldShowConsentSelector(state),
    content: state.activeConversation.visitorMessage,
    isMobile: isMobileSelector(state)
  }
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    sendMessage: (message) => dispatch(sendMessage(message)),
    sendTyping: (message) => dispatch(sendTyping(message)),
    sendTypingStopped: () => dispatch(sendTypingStopped()),
    uploadAndSendFile: (file) => dispatch(uploadAndSendFile(file)),
    updateMessageListHeight: (newHeight) =>
      dispatch(updateMessageListHeight(newHeight)),
    showConsent: () => dispatch(showConsent()),
    addPendingMessage: (message) => dispatch(addPendingMessage(message)),
    saveEditorContent: (content) => dispatch(saveEditorContent(content))
  }
}

interface FooterState {
  val: string
  showEmojiPicker: boolean
  showEmailConversation: boolean
}

class FooterComponent extends React.Component<FooterProps, FooterState> {
  references: {
    fileInput: HTMLInputElement
    messageInput: HTMLTextAreaElement
  } = {
    fileInput: null,
    messageInput: null
  }

  constructor(props: any) {
    super(props)

    this.state = {
      val: '',
      showEmojiPicker: false,
      showEmailConversation: false
    }
  }

  componentDidMount() {
    let height = 62
    if (this.references.messageInput.clientHeight !== 0) {
      height = this.references.messageInput.clientHeight
    }
    if (!this.props.onChange) {
      this.props.updateMessageListHeight(height + 2)
    }

    if (this.props.content) {
      this.setState({
        val: this.props.content
      })
    }
  }

  componentWillUnmount() {
    this.props.saveEditorContent(this.state.val)
  }

  sendTyping = throttle((message) => this.props.sendTyping(message), 250)
  onChange = throttle(() => this.props.onChange(), 500)

  handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prevValue = this.state.val
    const newValue = e.target.value

    if (newValue) {
      this.sendTyping(newValue)
    } else if (prevValue && !newValue) {
      this.props.sendTypingStopped()
    }

    this.setState({
      val: newValue
    })

    this.props.onChange && this.onChange()
  }

  handleTextareaChangeFocus = () => {
    this.props.onChange && this.onChange()
  }

  handleTextareaChangeBlur = () => {
    this.props.onChange && this.onChange()
  }

  handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (this.props.isActive && this.state.val) {
        if (this.shouldShowConsent()) {
          this.props.addPendingMessage(this.state.val)
          this.props.showConsent()
        } else {
          this.sendTyping.cancel()
          const msg = this.state.val && this.state.val.trim()
          this.props.sendMessage(msg)
        }

        this.setState(
          {
            val: ''
          },
          () => {
            this.props.afterSendMessage && this.props.afterSendMessage()
          }
        )
      }
    }
  }

  sendMessage = () => {
    if (this.props.isActive && this.state.val) {
      if (this.shouldShowConsent()) {
        this.props.addPendingMessage(this.state.val)
        this.props.showConsent()
      } else {
        this.sendTyping.cancel()
        this.props.sendMessage(this.state.val)
      }

      this.setState(
        {
          val: ''
        },
        () => {
          this.props.afterSendMessage && this.props.afterSendMessage()
        }
      )
    }
  }

  handleSendMessageClick = () => {
    this.sendMessage()
  }

  handleSendMessageKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.sendMessage()
    }
  }

  shouldShowConsent() {
    return (
      strings['onlineConsent'] !== undefined && this.props.shouldShowConsent
    )
  }

  emailConversation = () => {
    this.setState({
      showEmailConversation: !this.state.showEmailConversation,
      showEmojiPicker: false
    })
  }

  handleEmailConversationClick = (e: React.MouseEvent<HTMLDivElement>) => {
    this.emailConversation()
  }

  handleEmailConversationKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      this.emailConversation()
    }
  }

  handleEmojiClick = (e: React.MouseEvent<HTMLDivElement>) => {
    this.references.messageInput.focus()
    this.setState({
      showEmojiPicker: !this.state.showEmojiPicker,
      showEmailConversation: false
    })
  }

  handleEmojiKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      this.references.messageInput.focus()
      this.setState({
        showEmojiPicker: !this.state.showEmojiPicker,
        showEmailConversation: false
      })
    }
  }

  handleSmileyPicked = (data: any) => {
    let newState: any = {
      showEmojiPicker: false
    }

    if (this.props.isActive) {
      newState = {
        ...newState,
        val: this.state.val + data.value + ' '
      }
      this.references.messageInput.focus()
    }

    this.setState(newState)
  }

  handleUploadFileClick = (e: React.MouseEvent<HTMLDivElement>) => {
    this.references.fileInput.click()
  }

  handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.uploadAndSendFile(this.references.fileInput.files[0])
  }

  componentDidUpdate(prevProps) {
    let height = 62
    if (this.references.messageInput.clientHeight !== 0) {
      height = this.references.messageInput.clientHeight
    }
    if (!this.props.onChange) {
      this.props.updateMessageListHeight(height + 2)
    }

    if (prevProps.content !== this.props.content) {
      this.setState({
        val: this.props.content
      })
    }
  }

  collapseEmailConversation() {
    this.setState({ showEmailConversation: false })
  }

  handleEmailConversationClickOutside = () => {
    this.setState({ showEmailConversation: false })
  }

  handleEmailConversationCloseClick = () => {
    this.setState({ showEmailConversation: false })
  }

  handleAddFileKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.props.onAddFile && this.props.onAddFile()
    }
  }

  render() {
    let placeholder = strings.formatString(strings.chatMessagePlaceholder, {
      agentName: this.props.agentName
    }) as string

    if (!this.props.isActive) {
      placeholder = ''
    }

    const shouldShowSendButton =
      this.state.val.length > 0 && this.props.isActive
    const shouldShowEmailButton =
      !shouldShowSendButton && !this.props.isClosedByMe

    return (
      <div className='imbox-input-frame'>
        <input
          ref={(node) => (this.references.fileInput = node)}
          onChange={this.handleFileChange}
          type='file'
          className='hiddenFileInput'
        />
        <pre>{this.state.val}</pre>
        <textarea
          ref={(node) => (this.references.messageInput = node)}
          disabled={!this.props.isActive}
          placeholder={placeholder}
          value={this.state.val}
          onChange={this.handleTextareaChange}
          onFocus={this.handleTextareaChangeFocus}
          onBlur={this.handleTextareaChangeBlur}
          onKeyDown={this.handleKeyDown}
        />

        {!this.props.isMobile && this.state.showEmojiPicker && (
          <LoadableEmojiPicker
            search
            categories={[
              {
                category: 'custom',
                symbol: ''
              }
            ]}
            onClick={this.handleSmileyPicked}
            handleClickOutside={() => this.setState({ showEmojiPicker: false })}
            onEscapePress={() => this.setState({ showEmojiPicker: false })}
          />
        )}

        {this.state.showEmailConversation && (
          <div
            className='popover emailTranscriptPopover'
            style={{
              bottom: this.references.messageInput.clientHeight + 2 + 'px'
            }}
          >
            <EmailTranscriptPopover
              // eslint-disable-next-line react/jsx-handler-names
              handleClickOutside={this.handleEmailConversationClickOutside}
              // eslint-disable-next-line react/jsx-handler-names
              onClose={this.handleEmailConversationCloseClick}
            />
          </div>
        )}
        <div className='message-buttons'>
          {!this.props.isMobile && this.props.isActive && (
            <div
              className='message-button emoji-btn'
              onClick={this.handleEmojiClick}
              onKeyDown={this.handleEmojiKeyDown}
              role='button'
              tabIndex={0}
            >
              <div
                className='emoji-picker'
                onMouseOver={() =>
                  LoadableEmojiPicker.preload().catch((e) =>
                    console.warn('Failed to preload emojipicker', e)
                  )
                }
                onFocus={() =>
                  LoadableEmojiPicker.preload().catch((e) =>
                    console.warn('Failed to preload emojipicker', e)
                  )
                }
              >
                <i
                  aria-label={strings.a11yInsertEmoji}
                  className='icon-emoji-happy material-icons'
                >
                  insert_emoticon
                </i>
              </div>
            </div>
          )}

          {shouldShowSendButton && (
            <div
              className='message-button send-btn'
              onClick={this.handleSendMessageClick}
              tabIndex={0}
              role='button'
              aria-label={strings.a11yChatSendMessage}
              onKeyDown={this.handleSendMessageKeyDown}
            >
              <div className='send-message'>
                <i className='material-icons'>send</i>
              </div>
            </div>
          )}

          {shouldShowEmailButton && (
            <div
              className='message-button email-btn'
              onClick={this.handleEmailConversationClick}
              tabIndex={0}
              role='button'
              aria-label={strings.a11yChatEmailConversation}
              onKeyDown={this.handleEmailConversationKeyDown}
            >
              <div className='attachment'>
                <i className='icon-envelope-o material-icons'>mail_outline</i>
              </div>
            </div>
          )}

          {!this.shouldShowConsent() && this.props.isActive && (
            <div
              className='message-button upload-btn'
              onClick={this.props.onAddFile}
              role='button'
              tabIndex={0}
              aria-label={strings.a11yChatUploadFile}
              onKeyDown={this.handleAddFileKeyDown}
            >
              <div className='upload'>
                <i className='icon-attachment material-icons'>attach_file</i>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

const Footer = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(FooterComponent)

export { Footer }
